import React, { Suspense, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BlockStack,
  Box,
  Card,
  Checkbox,
  ChoiceList,
  Filters,
  InlineGrid,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris'
import { LockIcon, PlusIcon } from '@shopify/polaris-icons'
import { useQuery } from 'react-query'
import ExperienceTable from '../components/Section/components/ExperienceTable'
import { PAGE_TYPES } from '../constants/page.js'
import { useShopDetails } from '../hooks/useShopDetails.js'
import * as Recommendations from '../constants/widgets'
import { areFeaturesEnabled } from '../utils/features.js'
import { FEATURES } from '../constants/features.js'
import { PricingModal, usePricing } from '../components/PricingModal/index.js'
import DateTimeRangePopover from '../components/DateTimeRangePopover'
import { useLocalSettings } from '../components/LocalSettings'
import { getSectionsStats } from '../apis/analytics.js'
import SectionCreate from '../container/Section/Create/index.jsx'
import { useDashboardDetails } from '../hooks/useDashboardDetails.js'
import analytics, { MixPanelAnalytics } from '../utils/analytics.js'
import ErrorScreen from '../container/Error/index.jsx'

export default function SectionsList() {
  const breakpoints = useBreakpoints()
  const { t } = useTranslation()
  const [activeModal, setActiveModal] = useState(false)
  const [selectedExperienceId, setSelectedExperienceId] = useState(null)
  const toggleModal = (experienceId = null) => {
    setSelectedExperienceId(experienceId)
    setActiveModal((prev) => !prev)
  }
  const [showEnabledSections, setShowEnabledSections] = useState(null)
  const [statusFilter, setStatusFilter] = useState(undefined)
  const [recommendationFilter, setRecommendationFilter] = useState(undefined)
  const [pageFilter, setPageFilter] = useState(undefined)
  const [queryValue, setQueryValue] = useState(undefined)
  const { settings, saveSettings } = useLocalSettings()
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(new Date() - 7 * 24 * 60 * 60 * 1000),
    end: new Date(),
  })
  const { data: shopData, error, isLoading: shopDataLoading } = useShopDetails()
  const isABExperienceEnabled = areFeaturesEnabled(
    [FEATURES.AB_EXPERIENCE],
    window.shopify.data,
  )

  const sectionIds = shopData?.sections?.map((section) => section.id) ?? []
  const statsQuery = useQuery({
    queryKey: ['sections', sectionIds, selectedDates],
    queryFn: async ({ queryKey }) => {
      const [_, sectionIds, selectedDates] = queryKey
      const payload = {
        dateGte: new Date(selectedDates.start).valueOf() / 1000,
        dateLte: new Date(selectedDates.end).valueOf() / 1000,
        sections: sectionIds,
      }
      const { data, error } = await getSectionsStats(payload)
      if (error) {
        return Promise.reject(error)
      }
      return data.stats
    },
    enabled: sectionIds.length > 0,
  })

  const sectionWiseStates = statsQuery.data ?? {}
  const pricing = usePricing()

  const handleRecommendationFilterChange = useCallback(
    (value) => setRecommendationFilter(value),
    [],
  )
  const handlePageFilterChange = useCallback(
    (value) => setPageFilter(value),
    [],
  )
  const handleFiltersQueryChange = useCallback(
    (value) => setQueryValue(value),
    [],
  )
  const handleStatusFilterRemove = useCallback(
    () => setStatusFilter(undefined),
    [],
  )
  const handleRecommendationFilterRemove = useCallback(
    () => setRecommendationFilter(undefined),
    [],
  )
  const handlePageFilterRemove = useCallback(() => setPageFilter(undefined), [])
  const handleQueryValueRemove = useCallback(() => setQueryValue(undefined), [])
  const handelRemoveShowEnabledSections = useCallback(() => {
    setShowEnabledSections(null)
  }, [])
  const handleFiltersClearAll = useCallback(() => {
    handleStatusFilterRemove()
    handleRecommendationFilterRemove()
    handlePageFilterRemove()
    handleQueryValueRemove()
    handelRemoveShowEnabledSections()
  }, [
    handleStatusFilterRemove,
    handleQueryValueRemove,
    handleRecommendationFilterRemove,
    handlePageFilterRemove,
    handelRemoveShowEnabledSections,
  ])

  const filters = [
    {
      key: 'recommendation',
      label: t('Section.List.Filter.recommendationType'),
      filter: (
        <ChoiceList
          title={t('Section.List.Filter.recommendationType')}
          titleHidden
          choices={Object.values(Recommendations)
            .filter(({ value }) => Boolean(value))
            .map((rec) => ({
              label: t('Widgets.' + rec.value + '.label'),
              value: rec.value,
            }))}
          selected={recommendationFilter || []}
          onChange={handleRecommendationFilterChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'page',
      label: t('Section.List.Filter.page'),
      filter: (
        <ChoiceList
          title={t('Section.List.Filter.page')}
          titleHidden
          choices={[
            ...Object.entries(PAGE_TYPES).map(([key, obj]) => ({
              label: t(`Pages.${obj.value}.label`),
              value: obj.value,
            })),
          ]}
          selected={pageFilter || []}
          onChange={handlePageFilterChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'showEnabledSections',
      label: t('Section.List.enums.showEnabledSection'),
      filter: (
        <Checkbox
          checked={showEnabledSections}
          label={t('Section.List.enums.showEnabledSection')}
          onChange={() =>
            setShowEnabledSections(showEnabledSections ? null : true)
          }
        />
      ),
      pinned: true,
      shortcut: true,
    },
    {
      key: 'status',
      label: t('Section.List.Filter.status'),
      filter: (
        <ChoiceList
          title={t('Section.List.Filter.status')}
          titleHidden
          choices={[
            {
              label: t('Section.List.enums.sectionStatus.enabled'),
              value: true,
            },
            {
              label: t('Section.List.enums.sectionStatus.disabled'),
              value: false,
            },
          ]}
          selected={statusFilter || []}
          onChange={setStatusFilter}
          allowMultiple
        />
      ),
    },
  ]

  const appliedFilters = []

  if (!isEmpty(showEnabledSections)) {
    const key = 'showEnabledSections'
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, showEnabledSections),
      onRemove: handelRemoveShowEnabledSections,
    })
  }

  if (!isEmpty(recommendationFilter)) {
    const key = 'recommendation'
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, recommendationFilter),
      onRemove: handleRecommendationFilterRemove,
    })
  }

  if (!isEmpty(pageFilter)) {
    const key = 'page'
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, pageFilter),
      onRemove: handlePageFilterRemove,
    })
  }

  if (!isEmpty(statusFilter)) {
    const key = 'status'
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, statusFilter),
      onRemove: handleStatusFilterRemove,
    })
  }

  let experiences = (shopData?.experiences || []).map((exp) => ({
    ...exp,
    sections: [],
  }))

  ;(shopData?.sections || []).forEach((section) => {
    const experienceId = section.experience
    const idx = experiences.findIndex((exp) => exp.id === experienceId)
    const temp = {
      ...section,
      page: shopData.pages.find((page) => page.id === section.page).type,
    }
    experiences[idx]?.sections?.push(temp)
  })

  const totalExperienceWeights = experiences.reduce((acc, exp) => {
    return acc + exp.weight
  }, 0)

  const loading = shopDataLoading || statsQuery.isLoading

  if (error) {
    console.error('error', error)
    return <ErrorScreen />
  }

  return (
    <Page
      backAction={{
        content: 'back',
        onAction: () => {
          window.history.back()
        },
      }}
      title={t('Section.List.title')}
      subtitle={t('Section.List.subtitle')}
      primaryAction={{
        content: t('Section.List.cta'),
        icon: PlusIcon,
        url: '/sections/create',
        onAction: () => {
          analytics.trackButtonEvent(
            MixPanelAnalytics.Actions.CLICK,
            MixPanelAnalytics.Events.SECTION_CREATE_INITIATED,
          )
        },
      }}
      secondaryActions={[
        {
          content: t('Section.List.createExperienceCta'),
          url: isABExperienceEnabled && '/experience/create',
          icon: isABExperienceEnabled ? PlusIcon : LockIcon,
          onAction: () => {
            if (loading) return
            analytics.trackButtonEvent(
              MixPanelAnalytics.Actions.CLICK,
              MixPanelAnalytics.Events.EXPERIENCE_CREATE,
            )
            if (!isABExperienceEnabled) {
              pricing.open({
                features: [FEATURES.AB_EXPERIENCE],
              })
            }
          },
          loading: pricing.isLoading,
        },
        {
          content: t('Section.List.configureRec'),
          url: '/recommendation',
        },
      ]}
    >
      <Box padding={breakpoints.smUp ? '0' : '400'}>
        <Card padding={'0'}>
          {(experiences.length > 1 ||
            experiences?.[0]?.sections?.length > 0 ||
            loading) && (
            <>
              <Box paddingInline={'200'} paddingBlockStart={'200'}>
                <InlineGrid columns={'1fr auto'} gap={'200'}>
                  <TextField
                    placeholder={t('Section.List.Filter.queryPlaceholder')}
                    value={queryValue}
                    onChange={handleFiltersQueryChange}
                    disabled={loading}
                  />
                  <DateTimeRangePopover
                    handleDurationFilterChange={(duration) => {
                      saveSettings({ ...settings, durationFilter: duration })
                      setSelectedDates(duration)
                    }}
                    durationFilter={selectedDates}
                    disabled={loading}
                  />
                </InlineGrid>
              </Box>
              <Filters
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={handleFiltersClearAll}
                hideQueryField={true}
                disabled={loading}
              />
            </>
          )}
          <ExperienceTable
            experiences={experiences.map((exp) => ({
              ...exp,
              visitorPercentage: (
                (exp.weight / totalExperienceWeights) *
                100
              ).toFixed(2),
            }))}
            filters={{
              query: queryValue,
              status: statusFilter,
              recommendation: recommendationFilter,
              page: pageFilter,
              showEnabledSections,
            }}
            anyFiltersApplied={
              !isEmpty(queryValue) ||
              !isEmpty(statusFilter) ||
              !isEmpty(recommendationFilter) ||
              !isEmpty(pageFilter) ||
              showEnabledSections !== null
            }
            sectionStatsArray={sectionWiseStates}
            statsLoading={statsQuery.isLoading}
            shopLoading={shopDataLoading}
            activeModal={activeModal}
            selectedExperienceId={selectedExperienceId}
            disabled={loading}
          />
        </Card>
      </Box>
      <div
        style={{
          height: '1.5rem',
          width: '100%',
        }}
      ></div>
      <PricingModal modal={pricing} />
    </Page>
  )

  function disambiguateLabel(key, value) {
    switch (key) {
      case 'recommendation':
        return (
          'Recommendation: ' +
          value?.map((val) => t(`Widgets.${val}.label`)).join(', ')
        )
      case 'page':
        return (
          'Page: ' + value?.map((val) => t(`Pages.${val}.label`)).join(', ')
        )
      case 'status':
        return (
          'Status: ' +
          value
            ?.map((val) => t(`Section.List.enums.sectionStatus.${val}`))
            .join(', ')
        )
      case 'showEnabledSections':
        return value
          ? t('Section.List.enums.showEnabledSection') + ': True'
          : t('Section.List.enums.showEnabledSection') + ': False'
      default:
        return value
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0
    } else {
      return value === '' || value == null
    }
  }
}
